import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { Divider, Stack, Typography } from '@mui/material';

import ResolutionCategoryCareTaskDropdown from './ResolutionCategoryCareTaskDropdown';
import ResolutionCategoryItem from './ResolutionCategoryItem';
import ResolutionCategoryList from './ResolutionCategoryList';
import UnplannedCareNoteField from './UnplannedCareNoteField';
import groupBy from 'lodash/groupBy';
import { useAnalytics } from 'src/AnalyticsContext';
import { ResolutionCategoriesSorted, ResolutionTypes } from 'src/constants/ResolutionTypes';
import {
    ResidentCareFormValues,
    UnplannedCareRecord,
} from 'src/hooks/forms/useResidentCareTaskForm';
import useCareTasks from 'src/hooks/useCareTasks';
import useCareTypeFeatures from 'src/hooks/useCareTypeFeatures';
import useShiftInstances from 'src/hooks/useShiftInstances';
import { CareProvided as CareTaskCategory, Task } from 'src/store/sageAdminApi';

interface ResolutionCategoryFormContentProps {
    residentId: string;
    task: Task;
}

// Expects a FormContext with the useResidentCareTaskForm
const ResolutionCategoryFormContent = ({
    residentId,
    task,
}: ResolutionCategoryFormContentProps) => {
    // Ensure the care tasks we display are for the current shift instance, regardless of what the user may have selected previously
    const { currentTimeShift } = useShiftInstances();
    const { unresolvedCareTasks } = useCareTasks({
        residentIds: [residentId],
        enrichTasks: false,
        shiftInstanceRid: currentTimeShift?.id,
    });

    const { getFeatures } = useCareTypeFeatures();
    const ehrProvider = getFeatures(task?.unit?.careType).ehr.providerName;
    const { trackEvent } = useAnalytics();

    const { setValue, unregister } = useFormContext<ResidentCareFormValues>();
    const selectedResolutions: UnplannedCareRecord = useWatch({ name: 'unplannedCare' }) ?? {};
    const isTestAlert: boolean = useWatch({ name: 'isTestAlert' });

    const careTasksByCategory = useMemo(
        () => groupBy(unresolvedCareTasks, 'careTaskCategory'),
        [unresolvedCareTasks]
    );

    const handleSelectResolution = (resolutionId: CareTaskCategory, isChecked: boolean) => {
        trackEvent('Resolution Category Selected', {
            pageName: 'Alert Resolution',
            resolutionId,
            isChecked,
            residentId,
            taskId: task.id,
        });
        if (isChecked) {
            setValue(
                `unplannedCare.${resolutionId}`,
                { careTaskCategory: resolutionId },
                { shouldDirty: true }
            );
        } else {
            unregister(`unplannedCare.${resolutionId}`);
        }
    };

    const handleToggleTestAlert = (_: CareTaskCategory, isChecked: boolean) => {
        setValue('isTestAlert', isChecked, { shouldDirty: true });
    };

    return (
        <Stack>
            <ResolutionCategoryList
                categories={ResolutionCategoriesSorted}
                excludedResolutionTypes={[ResolutionTypes.RoutineCheck]}
                renderItem={(resolutionType) => {
                    const careTasks = careTasksByCategory[resolutionType.id];
                    return careTasks ? (
                        <ResolutionCategoryCareTaskDropdown
                            key={resolutionType.id}
                            resolutionType={resolutionType}
                            careTasks={careTasks}
                            ehrProvider={ehrProvider}
                            disabled={isTestAlert}
                            resolutionCategoryItemProps={{
                                isSelected:
                                    !!selectedResolutions[resolutionType.id]?.careTaskCategory,
                                onSelectOption: handleSelectResolution,
                                collapseContent: (
                                    <UnplannedCareNoteField categoryId={resolutionType.id} />
                                ),
                            }}
                        />
                    ) : (
                        <ResolutionCategoryItem
                            key={resolutionType.id}
                            resolutionType={resolutionType}
                            isSelected={!!selectedResolutions[resolutionType.id]?.careTaskCategory}
                            disabled={isTestAlert}
                            onSelectOption={handleSelectResolution}
                            collapseContent={
                                <UnplannedCareNoteField categoryId={resolutionType.id} />
                            }
                        />
                    );
                }}
            />
            {/* Test Alert */}
            <Stack sx={{ gap: 2, py: 2.5 }}>
                <Divider />
                <Stack sx={{ gap: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        System Test
                    </Typography>
                    <Typography variant="subtitle1">
                        A resident didn&apos;t set off this alert and nobody needs help.
                    </Typography>
                </Stack>
                <ResolutionCategoryItem
                    resolutionType={ResolutionTypes.FoundDevice}
                    isSelected={isTestAlert}
                    onSelectOption={handleToggleTestAlert}
                />
            </Stack>
        </Stack>
    );
};

export default ResolutionCategoryFormContent;
